/**
 * The scope vocabulary, and the two markers the catalogue prints.
 *
 * `ARCHITECTURE.md` §Scopes owns the table; this is it, written down once.
 * `apps/api/src/actions/vocab.ts` re-exports these three constants and adds the
 * domain list, the verb list and the name parser on top — the same arrangement
 * `CLIENT_ROLES` has, where the union lives here because both sides need it and the
 * API's file is the door everything in `apps/api` goes through.
 *
 * Why here rather than only in the API: an agent's scopes are a field on an entity
 * the studio portal renders and edits (session 20's agent form), and the schema that
 * validates that form has to be the same object the action's input is built from.
 */
import { z } from 'zod';
/**
 * Least to most trusted.
 *
 *   read      list, get, search, summary, compute
 *   tasks     the queue: claim, start, block, comment, log time, complete
 *   delivery  state changes that create no money
 *   money     anything that creates, moves or promises money
 *   admin     rate card, tiers, keys, credentials, refunds, members, agents, settings
 */
export const ACTION_SCOPES = ['read', 'tasks', 'delivery', 'money', 'admin'];
/**
 * `ARCHITECTURE.md` §Catalogue: "`$` needs `money` · `!` needs `admin` · everything
 * else is `read`, `tasks` or `delivery` by its verb."
 */
export const SCOPE_MARKERS = {
    read: '',
    tasks: '',
    delivery: '',
    money: '$',
    admin: '!',
};
/**
 * The four an owner may grant an agent. `ARCHITECTURE.md` §Scopes: "An agent holds
 * what its owner grants from the first four; `admin` is never grantable to an
 * agent."
 */
export const GRANTABLE_SCOPES = ['read', 'tasks', 'delivery', 'money'];
export const ActionScopeSchema = z
    .enum(ACTION_SCOPES)
    .describe('What a tool needs to be allowed to run: read, tasks, delivery, money ($) or admin (!). ' +
    'Staff hold all five (ARCHITECTURE.md, Scopes).');
export const GrantableScopeSchema = z
    .enum(GRANTABLE_SCOPES)
    .describe('A scope an agent may be granted: read (list, get, search, summary, compute), tasks ' +
    '(the queue), delivery (state changes that cost nothing) or money (anything that ' +
    'creates, moves or promises money, subject to the agent’s money limit). admin is ' +
    'never granted to an agent.');
/* ---------------------------------------------------------------- the domains */
/**
 * Every module domain an action name may start with — `ARCHITECTURE.md` §Catalogue's
 * first column.
 *
 * Here rather than only in `apps/api/src/actions/vocab.ts` for the reason the scopes
 * above are, and that file's header predicted it: session 20's studio portal decides
 * **which query keys a write invalidates** from the action's domain
 * (`apps/web/src/api/keys.ts` `moduleOfAction`), and `apps/web` may take types only
 * from `apps/api`. A second copy of this list in the SPA is a list that would
 * silently stop matching the day a module is added — and what it would do is stop
 * invalidating one module's queries, which shows as a studio page holding last
 * minute's numbers with no way to tell.
 *
 * The list is **closed and append-only**, and `vocab.ts` is still the door everything
 * in `apps/api` opens it through. The verb vocabulary stays there: nothing outside
 * the API needs to know what `submit_for_review` is.
 */
export const ACTION_DOMAINS = [
    'agents',
    'clients',
    'catalog',
    'retainers',
    'deliverables',
    'content_plans',
    'work_orders',
    'widgets',
    'usage',
    'credits',
    'invoices',
    'payments',
    'billing',
    'files',
    'calls',
    'reports',
    'tasks',
    'notifications',
    'analytics',
    'expenses',
    'audit',
    'settings',
    /*
     * Session 46. Outbound site hooks from a portal-hosted widget — a GitHub dispatch or
     * a signed HTTP POST (`.docs/46-blog-and-forms.md` §5). Its own domain rather than a
     * verb under `widgets`, because a hook is a row of its own with its own delivery log,
     * and `widgets_create_hook` / `widgets_list_hook_deliveries` would read as things
     * done to the widget rather than to the hook.
     */
    'hooks',
    /*
     * Session 46. A portal-hosted blog's posts, revisions, authors and media
     * (`.docs/46-blog-and-forms.md` §4) — the studio writes a client's posts for them, and
     * a client may have no portal login at all, so the whole workspace is a set of tools.
     */
    'blog',
    /*
     * Session 46. A portal-hosted forms widget's forms and the submissions they took
     * (`.docs/46-blog-and-forms.md` §6) — the studio configures a client's contact form for
     * them and triages its leads, for the same reason it writes a client's posts.
     */
    'forms',
    /*
     * Session 48. Mockups — HTML the studio shares with a client for feedback
     * (`.docs/48-mockups.md`): the design, its versions, the comments, its link. Its own
     * domain rather than a kind of file, because a mockup is many files, is viewed rather than
     * downloaded, and is answered per version.
     */
    'mockups',
];
/**
 * The longest `<value>` or `<value>_…` prefix of `text`, or null.
 *
 * Exported for its own test, and for `vocab.ts`'s verb half. No element of
 * `ACTION_DOMAINS` is currently an underscore-prefix of another and neither is any
 * verb, so a first-match loop would agree with this one on every name that exists
 * today — which means a test over real names cannot tell the rule from the accident.
 * `work_orders` vs a future `work`, and `submit_for_review` vs a future `submit`, are
 * why the rule is written this way.
 */
export function longestPrefix(text, values) {
    let found = null;
    for (const value of values) {
        if (text === value || text.startsWith(`${value}_`)) {
            if (found === null || value.length > found.length)
                found = value;
        }
    }
    return found;
}
/**
 * The module domain an action name belongs to, or null when it starts with none.
 *
 * `content_plans_draft` is `content_plans`, not `content`; `work_orders_price` is
 * `work_orders`, not `work`. The SPA reads this to decide what a write invalidated;
 * `vocab.ts` `splitActionName` reads it and then goes on to the verb.
 */
export function domainOfAction(name) {
    return longestPrefix(name, ACTION_DOMAINS);
}
/* ------------------------------------------------------------ needs review */
/**
 * What a `$` tool answers with when it was held for a person instead of run.
 *
 * `ARCHITECTURE.md` §Scopes: "a `money` action above it is not executed — the
 * prepared payload becomes a `review` task for the agent's owner and the tool returns
 * `{ status: "needs_review", task_ref }`."
 *
 * It is a **result**, not an error (session 05 had it as one, because there was no
 * `tasks` module to raise the task in). An agent branches on `status`: anything else
 * is the tool's own output, and this means somebody has been asked and the answer will
 * arrive as a person doing it. Retrying does not help; the same idempotency key gives
 * back the same task.
 *
 * `defineAction` adds it to the declared output of every `money` action, so an MCP
 * client validating `structuredContent` against the published schema accepts both
 * shapes (`apps/api/src/actions/define.ts`, `answers`).
 */
export const NeedsReviewResultSchema = z
    .strictObject({
    status: z
        .literal('needs_review')
        .describe('Always "needs_review". Anything else is the tool’s own answer.'),
    task_ref: z
        .string()
        .describe('The review task a person will work from, TSK-NNNN. Nothing has been done yet.'),
    reason: z
        .string()
        .describe('Why it was held, in a sentence — usually the money limit and the amount.'),
})
    .describe('The call was accepted and turned into a task for a person; nothing has run.');
/** True for the answer above, whatever else a tool's output could be. */
export function isNeedsReview(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value.status === 'needs_review');
}
//# sourceMappingURL=actions.js.map